import React, { useState, useEffect } from 'react';
import Logo from './Logo';
import InvestmentDetail from './InvestmentDetail';
import TabunganSummary from './TabunganSummary';
import TabunganQurban from './TabunganQurban';
import Pembayaran from './Pembayaran';
import Transaksi from './Transaksi';
import PromoBerita from './PromoBerita';
import AccountPage from './AccountPage';
import apiClient from '../utils/apiClient';
import { API_ENDPOINTS } from '../config/api';

const MemberArea = () => {
  const [activeBottomTab, setActiveBottomTab] = useState('home');
  const [showInvestmentDetail, setShowInvestmentDetail] = useState(false);
  const [showTabunganSummary, setShowTabunganSummary] = useState(false);
  const [showTabunganQurban, setShowTabunganQurban] = useState(false);
  const [showPembayaran, setShowPembayaran] = useState(false);
  const [showTransaksi, setShowTransaksi] = useState(false);
  const [showPromoBerita, setShowPromoBerita] = useState(false);
  const [showAccountPage, setShowAccountPage] = useState(false);
  const [selectedPromoFromMemberArea, setSelectedPromoFromMemberArea] = useState(null);
  const [selectedTabungan, setSelectedTabungan] = useState(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [userData, setUserData] = useState({
    full_name: 'Loading...',
    email: '',
    id: null
  });
  const [userLoading, setUserLoading] = useState(true);
  const [promos, setPromos] = useState([]);
  const [promosLoading, setPromosLoading] = useState(true);
  const [tabunganSummary, setTabunganSummary] = useState({
    totalEkor: 0,
    totalHarga: 0,
    loading: true
  });
  const [investasiSummary, setInvestasiSummary] = useState({
    totalInvestasi: 0,
    totalInvoice: 0,
    loading: true
  });

  // Fetch user data dari API
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setUserLoading(true);
        // Coba ambil dari user_data di localStorage dulu (dari login response)
        const savedUserData = localStorage.getItem('user_data');
        if (savedUserData) {
          try {
            const parsed = JSON.parse(savedUserData);
            if (parsed.id) {
              // Fetch detail user dari API menggunakan user_id
              const response = await apiClient.get(API_ENDPOINTS.USER.GET_BY_ID(parsed.id));
              
              // Handle berbagai format response
              let userFromApi = null;
              if (response.data && response.data.user) {
                userFromApi = response.data.user;
              } else if (response.data && response.data.data) {
                userFromApi = response.data.data;
              } else if (response.data && !response.data.user) {
                userFromApi = response.data;
              } else if (!response.data) {
                userFromApi = response;
              }
              
              if (userFromApi) {
                setUserData({
                  full_name: userFromApi.full_name || parsed.full_name || 'User',
                  email: userFromApi.email || parsed.email || '',
                  id: userFromApi.id || parsed.id
                });
              } else {
                // Fallback ke data dari localStorage
                setUserData({
                  full_name: parsed.full_name || 'User',
                  email: parsed.email || '',
                  id: parsed.id
                });
              }
            } else {
              // Gunakan data dari localStorage
              setUserData({
                full_name: parsed.full_name || 'User',
                email: parsed.email || '',
                id: parsed.id
              });
            }
          } catch (error) {
            console.error('Error fetching user by ID:', error);
            // Coba fetch dari /api/users/me
            try {
              const meResponse = await apiClient.get(API_ENDPOINTS.USER.GET_ME);
              if (meResponse.data && meResponse.data.data) {
                const userMe = meResponse.data.data;
                setUserData({
                  full_name: userMe.full_name || 'User',
                  email: userMe.email || '',
                  id: userMe.id
                });
              }
            } catch (meError) {
              console.error('Error fetching user me:', meError);
              // Fallback ke data dari localStorage
              const parsed = JSON.parse(savedUserData);
              setUserData({
                full_name: parsed.full_name || 'User',
                email: parsed.email || '',
                id: parsed.id
              });
            }
          }
        } else {
          // Jika tidak ada di localStorage, fetch dari /api/users/me
          const meResponse = await apiClient.get(API_ENDPOINTS.USER.GET_ME);
          if (meResponse.data && meResponse.data.data) {
            const userMe = meResponse.data.data;
            setUserData({
              full_name: userMe.full_name || 'User',
              email: userMe.email || '',
              id: userMe.id
            });
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        // Fallback ke default
        setUserData({
          full_name: 'User',
          email: '',
          id: null
        });
      } finally {
        setUserLoading(false);
      }
    };

    fetchUserData();
  }, []);

  // Fetch promo & berita dari API
  useEffect(() => {
    const fetchPromos = async () => {
      try {
        setPromosLoading(true);
        
        // Build query parameters - ambil 2 artikel terbaru untuk preview
        const params = new URLSearchParams({
          limit: '2',
          offset: '0'
        });

        const response = await apiClient.get(`${API_ENDPOINTS.ARTIKEL.PUBLIC_LIST}?${params.toString()}`);

        // Handle response structure
        let articlesFromApi = [];
        if (response.data && response.data.artikels && Array.isArray(response.data.artikels)) {
          articlesFromApi = response.data.artikels;
        } else if (response.data && Array.isArray(response.data)) {
          articlesFromApi = response.data;
        } else if (Array.isArray(response)) {
          articlesFromApi = response;
        }

        // Filter hanya artikel yang published
        const publishedArticles = articlesFromApi.filter(article => {
          return article.is_published === true && article.status !== 'draft';
        });

        // Transform data artikel ke format yang dibutuhkan UI untuk preview di MemberArea
        const formattedPromos = publishedArticles.slice(0, 2).map((article, index) => {
          // Tentukan badge berdasarkan kategori atau is_featured
          let badge = article.category || 'Berita';
          let badgeColor = 'bg-red-100 text-red-600'; // Default untuk promo
          
          if (article.is_featured) {
            badge = 'Featured';
            badgeColor = 'bg-yellow-100 text-yellow-600';
          } else if (article.category) {
            const categoryLower = article.category.toLowerCase();
            if (categoryLower.includes('promo') || categoryLower.includes('diskon')) {
              badgeColor = 'bg-red-100 text-red-600';
            } else if (categoryLower.includes('investasi')) {
              badgeColor = 'bg-blue-100 text-blue-600';
            }
          }

          return {
            id: article.id || article.artikel_id || index,
            image: article.featured_image || '/sapi1.jpg',
            badge: badge,
            badgeColor: badgeColor,
            title: article.title || 'Judul Artikel',
            description: article.excerpt || article.content?.substring(0, 60) + '...' || 'Deskripsi artikel',
            date: article.created_at ? new Date(article.created_at).toLocaleDateString('id-ID', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            }) : '',
            category: article.category || 'Berita',
            content: article.content || article.excerpt || 'Konten artikel tidak tersedia',
            slug: article.slug,
            validUntil: null,
            is_featured: article.is_featured || false
          };
        });

        setPromos(formattedPromos);
      } catch (error) {
        console.error('Error fetching promos:', error);
        // Fallback ke data kosong jika API error
        setPromos([]);
      } finally {
        setPromosLoading(false);
      }
    };

    fetchPromos();
  }, []);

  // Fetch tabungan summary dari API
  useEffect(() => {
    const fetchTabunganSummary = async () => {
      try {
        setTabunganSummary(prev => ({ ...prev, loading: true }));
        
        let userId = userData.id;
        if (!userId) {
          const savedUserData = localStorage.getItem('user_data');
          if (savedUserData) {
            const parsed = JSON.parse(savedUserData);
            userId = parsed.id;
          }
        }

        if (!userId) {
          setTabunganSummary({
            totalEkor: 0,
            totalHarga: 0,
            loading: false
          });
          return;
        }

        // Build query parameters
        const params = new URLSearchParams({
          user_id: userId
        });

        const response = await apiClient.get(`${API_ENDPOINTS.TABUNGAN.LIST}?${params.toString()}`);

        // Handle response structure
        let tabunganData = [];
        if (response.data && response.data.tabungans && Array.isArray(response.data.tabungans)) {
          tabunganData = response.data.tabungans;
        } else if (response.data && Array.isArray(response.data)) {
          tabunganData = response.data;
        } else if (Array.isArray(response)) {
          tabunganData = response;
        }

        // Filter hanya tabungan aktif (deleted_at === null)
        const tabunganAktif = tabunganData.filter(item => item.deleted_at === null);

        // Hitung total ekor
        const totalEkor = tabunganAktif.length;

        // Fetch harga produk untuk setiap tabungan yang punya produk_id
        let totalHarga = 0;
        const produkIds = tabunganAktif
          .map(item => item.produk_id)
          .filter(id => id !== null && id !== undefined);

        if (produkIds.length > 0) {
          try {
            // Fetch produk details untuk semua produk_id yang unik
            const uniqueProdukIds = [...new Set(produkIds)];
            const produkPromises = uniqueProdukIds.map(async (produkId) => {
              try {
                const produkResponse = await apiClient.get(API_ENDPOINTS.PRODUK.PUBLIC_DETAIL(produkId));
                
                // Handle response structure
                let produk = null;
                if (produkResponse.data && produkResponse.data.produk) {
                  produk = produkResponse.data.produk;
                } else if (produkResponse.data && !produkResponse.data.produk) {
                  produk = produkResponse.data;
                } else if (!produkResponse.data) {
                  produk = produkResponse;
                }
                
                return {
                  produkId,
                  hargaBeli: produk?.harga_beli || produk?.price || produk?.harga || 0
                };
              } catch (error) {
                console.error(`Error fetching produk ${produkId}:`, error);
                return {
                  produkId,
                  hargaBeli: 0
                };
              }
            });

            const produkPrices = await Promise.all(produkPromises);
            
            // Map produk_id ke harga_beli
            const hargaMap = {};
            produkPrices.forEach(item => {
              hargaMap[item.produkId] = item.hargaBeli;
            });

            // Hitung total harga dari semua tabungan
            totalHarga = tabunganAktif.reduce((sum, tabungan) => {
              if (tabungan.produk_id && hargaMap[tabungan.produk_id]) {
                return sum + (typeof hargaMap[tabungan.produk_id] === 'number' 
                  ? hargaMap[tabungan.produk_id] 
                  : parseFloat(hargaMap[tabungan.produk_id]) || 0);
              }
              return sum;
            }, 0);
          } catch (error) {
            console.error('Error fetching produk prices:', error);
            totalHarga = 0;
          }
        }

        setTabunganSummary({
          totalEkor,
          totalHarga,
          loading: false
        });
      } catch (error) {
        console.error('Error fetching tabungan summary:', error);
        setTabunganSummary({
          totalEkor: 0,
          totalHarga: 0,
          loading: false
        });
      }
    };

    if (userData.id) {
      fetchTabunganSummary();
    }
  }, [userData.id]);

  // Fetch investasi summary dari API
  useEffect(() => {
    const fetchInvestasiSummary = async () => {
      try {
        setInvestasiSummary(prev => ({ ...prev, loading: true }));
        
        let userId = userData.id;
        if (!userId) {
          const savedUserData = localStorage.getItem('user_data');
          if (savedUserData) {
            const parsed = JSON.parse(savedUserData);
            userId = parsed.id;
          }
        }

        if (!userId) {
          setInvestasiSummary({
            totalInvestasi: 0,
            totalInvoice: 0,
            loading: false
          });
          return;
        }

        // Fetch investasi dari endpoint /api/investasi/user/{user_id}
        const investasiResponse = await apiClient.get(API_ENDPOINTS.INVESTASI.BY_USER(userId));

        // Handle response structure investasi
        let investasiData = [];
        if (investasiResponse.data && investasiResponse.data.investasis && Array.isArray(investasiResponse.data.investasis)) {
          investasiData = investasiResponse.data.investasis;
        } else if (investasiResponse.data && Array.isArray(investasiResponse.data)) {
          investasiData = investasiResponse.data;
        } else if (Array.isArray(investasiResponse)) {
          investasiData = investasiResponse;
        }

        // Filter hanya investasi aktif (deleted_at === null)
        const investasiAktif = investasiData.filter(item => item.deleted_at === null);

        // Hitung jumlah invoice (jumlah investasi)
        const totalInvoice = investasiAktif.length;

        // Fetch investasi-produk untuk setiap investasi dari endpoint /api/investasi-produk/investasi/{investasi_id}
        let allInvestasiProdukData = [];
        
        if (investasiAktif.length > 0) {
          try {
            const investasiProdukPromises = investasiAktif.map(async (investasi) => {
              try {
                // Ambil ID investasi - bisa berupa id atau investasi_id
                const investasiId = investasi.id || investasi.investasi_id;
                if (!investasiId) {
                  console.warn('Investasi tidak memiliki ID:', investasi);
                  return [];
                }
                
                const investasiProdukResponse = await apiClient.get(API_ENDPOINTS.INVESTASI_PRODUK.BY_INVESTASI(investasiId));

                // Handle response structure investasi-produk
                let investasiProdukData = [];
                if (investasiProdukResponse.data && investasiProdukResponse.data.investasi_produks && Array.isArray(investasiProdukResponse.data.investasi_produks)) {
                  investasiProdukData = investasiProdukResponse.data.investasi_produks;
                } else if (investasiProdukResponse.data && Array.isArray(investasiProdukResponse.data)) {
                  investasiProdukData = investasiProdukResponse.data;
                } else if (Array.isArray(investasiProdukResponse)) {
                  investasiProdukData = investasiProdukResponse;
                }

                return investasiProdukData;
              } catch (error) {
                console.error(`Error fetching investasi-produk untuk investasi ${investasi.id}:`, error);
                return [];
              }
            });

            const allInvestasiProdukResults = await Promise.all(investasiProdukPromises);
            allInvestasiProdukData = allInvestasiProdukResults.flat();
          } catch (error) {
            console.error('Error fetching investasi-produk:', error);
          }
        }

        // Fetch harga produk untuk setiap investasi-produk yang punya produk_id
        let totalInvestasi = 0;
        const produkIds = allInvestasiProdukData
          .map(item => item.produk_id)
          .filter(id => id !== null && id !== undefined);

        if (produkIds.length > 0) {
          try {
            // Fetch produk details untuk semua produk_id yang unik
            const uniqueProdukIds = [...new Set(produkIds)];
            const produkPromises = uniqueProdukIds.map(async (produkId) => {
              try {
                const produkResponse = await apiClient.get(API_ENDPOINTS.PRODUK.PUBLIC_DETAIL(produkId));
                
                // Handle response structure
                let produk = null;
                if (produkResponse.data && produkResponse.data.produk) {
                  produk = produkResponse.data.produk;
                } else if (produkResponse.data && !produkResponse.data.produk) {
                  produk = produkResponse.data;
                } else if (!produkResponse.data) {
                  produk = produkResponse;
                }
                
                return {
                  produkId,
                  hargaBeli: produk?.harga_beli || produk?.price || produk?.harga || 0
                };
              } catch (error) {
                console.error(`Error fetching produk ${produkId}:`, error);
                return {
                  produkId,
                  hargaBeli: 0
                };
              }
            });

            const produkPrices = await Promise.all(produkPromises);
            
            // Map produk_id ke harga_beli
            const hargaMap = {};
            produkPrices.forEach(item => {
              hargaMap[item.produkId] = item.hargaBeli;
            });

            // Hitung total investasi dari semua investasi-produk berdasarkan harga produk
            totalInvestasi = allInvestasiProdukData.reduce((sum, investasiProduk) => {
              if (investasiProduk.produk_id && hargaMap[investasiProduk.produk_id]) {
                return sum + (typeof hargaMap[investasiProduk.produk_id] === 'number' 
                  ? hargaMap[investasiProduk.produk_id] 
                  : parseFloat(hargaMap[investasiProduk.produk_id]) || 0);
              }
              return sum;
            }, 0);
          } catch (error) {
            console.error('Error fetching produk prices:', error);
            totalInvestasi = 0;
          }
        }

        setInvestasiSummary({
          totalInvestasi,
          totalInvoice,
          loading: false
        });
      } catch (error) {
        console.error('Error fetching investasi summary:', error);
        setInvestasiSummary({
          totalInvestasi: 0,
          totalInvoice: 0,
          loading: false
        });
      }
    };

    if (userData.id) {
      fetchInvestasiSummary();
    }
  }, [userData.id]);

  // Fungsi logout
  const handleLogout = async () => {
    if (!window.confirm('Apakah Anda yakin ingin keluar?')) {
      return;
    }

    try {
      // Hit API logout
      await apiClient.post(API_ENDPOINTS.AUTH.LOGOUT);
    } catch (error) {
      console.error('Error calling logout API:', error);
      // Tetap lanjutkan proses logout meskipun API error
    } finally {
      // Hapus semua data dari localStorage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user_data');
      
      // Remove token dari apiClient
      apiClient.removeAuthToken();
      
      // Redirect ke halaman login
      window.location.href = '/login';
    }
    
    setShowUserDropdown(false);
  };

  // Effect untuk menutup dropdown ketika klik di luar
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserDropdown && !event.target.closest('.user-dropdown-container')) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);

  const services = [
    { id: 1, icon: 'fa-wallet', label: 'Tabungan' },
    { id: 2, icon: 'fa-sack-dollar', label: 'Investasi' },
    { id: 3, icon: 'fa-cow', label: 'Katalog' },
    { id: 4, icon: 'fa-headset', label: 'Konsultasi' },
    { id: 5, icon: 'fa-circle-question', label: 'FAQ' },
    { id: 6, icon: 'fa-home', label: 'Landing Page' }
  ];

  // Jika sedang menampilkan detail investasi
  if (showInvestmentDetail) {
    return (
      <InvestmentDetail 
        onBack={() => setShowInvestmentDetail(false)}
        activeBottomTab={activeBottomTab}
        setActiveBottomTab={setActiveBottomTab}
      />
    );
  }

  // Jika sedang menampilkan summary tabungan
  if (showTabunganSummary) {
    return (
      <TabunganSummary 
        onBack={() => setShowTabunganSummary(false)}
        onViewDetail={(tabungan) => {
          setSelectedTabungan(tabungan);
          setShowTabunganSummary(false);
          setShowTabunganQurban(true);
        }}
        activeBottomTab={activeBottomTab}
        setActiveBottomTab={setActiveBottomTab}
      />
    );
  }

  // Jika sedang menampilkan detail tabungan qurban
  if (showTabunganQurban) {
    return (
      <TabunganQurban 
        onBack={() => {
          setShowTabunganQurban(false);
          setSelectedTabungan(null);
          setShowTabunganSummary(true);
        }}
        selectedTabungan={selectedTabungan}
        activeBottomTab={activeBottomTab}
        setActiveBottomTab={setActiveBottomTab}
      />
    );
  }

  // Jika sedang menampilkan pembayaran
  if (showPembayaran) {
    return (
      <Pembayaran 
        onBack={() => setShowPembayaran(false)}
        activeBottomTab={activeBottomTab}
        setActiveBottomTab={setActiveBottomTab}
      />
    );
  }

  // Jika sedang menampilkan transaksi
  if (showTransaksi) {
    return (
      <Transaksi 
        onBack={() => setShowTransaksi(false)}
        activeBottomTab={activeBottomTab}
        setActiveBottomTab={setActiveBottomTab}
      />
    );
  }

  // Jika sedang menampilkan promo & berita
  if (showPromoBerita) {
    return (
      <PromoBerita 
        onBack={() => {
          setShowPromoBerita(false);
          setSelectedPromoFromMemberArea(null); // Reset ketika kembali
        }}
        activeBottomTab={activeBottomTab}
        setActiveBottomTab={setActiveBottomTab}
        initialSelectedPromo={selectedPromoFromMemberArea} // Pass promo yang dipilih
        fromMemberArea={!!selectedPromoFromMemberArea} // True jika ada selectedPromoFromMemberArea
      />
    );
  }

  // Jika sedang menampilkan halaman akun
  if (showAccountPage) {
    return (
      <AccountPage 
        onBack={() => setShowAccountPage(false)}
        activeBottomTab={activeBottomTab}
        setActiveBottomTab={setActiveBottomTab}
        userData={userData}
      />
    );
  }

  return (
    <section id="member-area" className="min-h-screen bg-white">
      {/* Mobile Wrapper */}
      <div className="max-w-lg mx-auto bg-white shadow-2xl min-h-screen">
        
        {/* Header */}
        <header className="px-4 pt-8 pb-4 bg-white">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-2">
                <Logo size="small" showText={false} />
                <span className="font-bold text-primary">PAK MoO</span>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="relative user-dropdown-container">
                <div 
                  className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 rounded-lg p-2 transition-colors"
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                >
                  <div className="text-right">
                    <p className="text-sm font-semibold text-primary">{userData.full_name}</p>
                    <p className="text-xs text-gray-500">{userData.email || 'Email tidak tersedia'}</p>
                  </div>
                  <div className="w-10 h-10 rounded-full border-2 border-accent bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                    <i className="fa-solid fa-user text-primary text-lg"></i>
                  </div>
                  <i className={`fa-solid fa-chevron-down text-gray-400 text-sm transition-transform ${showUserDropdown ? 'rotate-180' : ''}`}></i>
                </div>
                
                {/* Dropdown Menu */}
                {showUserDropdown && (
                  <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-100 z-50">
                    <div className="p-4 border-b border-gray-100">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 rounded-full border-2 border-accent bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                          <i className="fa-solid fa-user text-primary text-xl"></i>
                        </div>
                        <div>
                          <p className="font-semibold text-primary">{userData.full_name}</p>
                          <p className="text-xs text-gray-500">{userData.email || 'Email tidak tersedia'}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-2">
                      <button 
                        className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors text-left"
                        onClick={() => {
                          setShowUserDropdown(false);
                          setActiveBottomTab('account');
                          setShowAccountPage(true);
                        }}
                      >
                        <i className="fa-solid fa-user-circle text-primary"></i>
                        <span className="font-medium text-gray-800">Lihat Profil</span>
                      </button>
                      
                      <button 
                        className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors text-left"
                        onClick={() => {
                          setShowUserDropdown(false);
                          window.open('/kontak', '_blank');
                        }}
                      >
                        <i className="fa-solid fa-question-circle text-primary"></i>
                        <span className="font-medium text-gray-800">Bantuan</span>
                      </button>
                      
                      <hr className="my-2 border-gray-100" />
                      
                      <button 
                        className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-red-50 transition-colors text-left text-red-600"
                        onClick={handleLogout}
                      >
                        <i className="fa-solid fa-sign-out-alt"></i>
                        <span className="font-medium">Keluar</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="pb-32 bg-white">
          {/* Hero Carousel */}
          <section className="px-4 mt-4">
            <div className="relative rounded-2xl shadow-lg overflow-hidden h-[200px]">
              <div className="absolute inset-0 w-full h-full">
                <img className="w-full h-full object-cover" src="/sapi1.jpg" alt="Sapi berkualitas PAK MoO" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent"></div>
              <div className="absolute bottom-4 left-4 right-4">
                <h2 className="text-white font-bold text-xl leading-tight">Solusi Ternak Modern Anda</h2>
                <p className="text-gray-200 text-sm mt-1">Kualitas terbaik dari peternak terpercaya.</p>
              </div>
            </div>
          </section>

          {/* Info Cards */}
          <section className="px-4 mt-6 space-y-4">
            <div 
              className="bg-secondary rounded-2xl shadow-lg p-4 flex items-center justify-between text-white cursor-pointer hover:shadow-xl transition-shadow"
              onClick={() => setShowTabunganSummary(true)}
            >
              <div className="flex items-center space-x-4">
                <div className="bg-accent/80 p-3 rounded-xl">
                  <i className="fa-solid fa-hand-holding-dollar text-primary text-2xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold">Tabungan Qurban</h4>
                  {tabunganSummary.loading ? (
                    <>
                      <p className="text-2xl font-bold mt-1">...</p>
                      <p className="text-sm opacity-80">Memuat...</p>
                    </>
                  ) : (
                    <>
                      <p className="text-2xl font-bold mt-1">
                        {tabunganSummary.totalEkor} {tabunganSummary.totalEkor === 1 ? 'Ekor' : 'Ekor'}
                      </p>
                      <p className="text-sm opacity-80">
                        {tabunganSummary.totalHarga > 0 
                          ? new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(tabunganSummary.totalHarga)
                          : 'Rp 0'}
                      </p>
                    </>
                  )}
                </div>
              </div>
              <i className="fa-solid fa-chevron-right text-accent text-3xl opacity-70"></i>
            </div>

            <div 
              className="bg-secondary rounded-2xl shadow-lg p-4 flex items-center justify-between text-white cursor-pointer hover:shadow-xl transition-shadow"
              onClick={() => setShowInvestmentDetail(true)}
            >
              <div className="flex items-center space-x-4">
                <div className="bg-accent/80 p-3 rounded-xl">
                  <i className="fa-solid fa-chart-line text-primary text-2xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold">Mitra Ternak</h4>
                  {investasiSummary.loading ? (
                    <>
                      <p className="text-2xl font-bold mt-1">...</p>
                      <p className="text-sm opacity-80">Memuat...</p>
                    </>
                  ) : (
                    <>
                      <p className="text-2xl font-bold mt-1">
                        {investasiSummary.totalInvestasi > 0 
                          ? new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(investasiSummary.totalInvestasi)
                          : 'Rp 0'}
                      </p>
                      <p className="text-sm opacity-80">
                        Total Investasi {investasiSummary.totalInvoice} {investasiSummary.totalInvoice === 1 ? 'Invoice' : 'Invoice'}
                      </p>
                    </>
                  )}
                </div>
              </div>
              <i className="fa-solid fa-chevron-right text-accent text-3xl opacity-70"></i>
            </div>
          </section>

          {/* Services Grid */}
          <section className="px-4 mt-8">
            <div className="grid grid-cols-3 gap-y-6 gap-x-4 text-center">
              {services.map((service) => (
                <div 
                  key={service.id} 
                  className="flex flex-col items-center cursor-pointer"
                  onClick={() => {
                    if (service.label === 'Tabungan') {
                      setShowTabunganSummary(true);
                    } else if (service.label === 'Investasi') {
                      setShowInvestmentDetail(true);
                    } else if (service.label === 'Katalog') {
                      // Redirect ke katalog sapi
                      window.location.href = '/katalog';
                    } else if (service.label === 'Konsultasi') {
                      // Redirect to WhatsApp
                      window.open('https://wa.me/6281234567890?text=Halo,%20saya%20ingin%20konsultasi%20tentang%20produk%20PAK%20MoO', '_blank');
                    } else if (service.label === 'FAQ') {
                      // Redirect ke halaman FAQ
                      window.location.href = '/faq';
                    } else if (service.label === 'Landing Page') {
                      // Redirect ke landing page utama
                      window.location.href = '/';
                    }
                  }}
                >
                  <div className="bg-secondary shadow-md rounded-2xl w-16 h-16 flex items-center justify-center hover:bg-primary transition-colors">
                    <i className={`fa-solid ${service.icon} text-accent text-3xl`}></i>
                  </div>
                  <p className="text-xs font-medium text-gray-600 mt-2">{service.label}</p>
                </div>
              ))}
            </div>
          </section>
          
          {/* Promo Section */}
          <section className="px-4 mt-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-primary">Promo & Berita</h3>
              <span 
                className="text-sm font-semibold text-accent cursor-pointer"
                onClick={() => setShowPromoBerita(true)}
              >
                Lihat Semua
              </span>
            </div>
            <div className="space-y-4">
              {promosLoading ? (
                <div className="bg-white rounded-2xl shadow-lg p-3 flex items-center space-x-4 animate-pulse">
                  <div className="w-20 h-20 bg-gray-300 rounded-xl"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                    <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                  </div>
                </div>
              ) : promos.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
                  <i className="fa-solid fa-newspaper text-gray-400 text-3xl mb-2"></i>
                  <p className="text-gray-500 text-sm">Belum ada promo atau berita tersedia.</p>
                </div>
              ) : (
                promos.map((promo) => (
                <div 
                  key={promo.id} 
                  className="bg-white rounded-2xl shadow-lg p-3 flex items-center space-x-4 cursor-pointer hover:shadow-xl transition-shadow"
                  onClick={() => {
                    // Convert promo data to match DetailPromo format
                    const detailPromoData = {
                      id: promo.id,
                      image: promo.image,
                      badge: promo.badge,
                      badgeColor: promo.badgeColor,
                      title: promo.title,
                      description: promo.description,
                      date: promo.date,
                      category: promo.category,
                      content: promo.content,
                      validUntil: promo.validUntil,
                      slug: promo.slug,
                      is_featured: promo.is_featured
                    };
                    setSelectedPromoFromMemberArea(detailPromoData);
                    setShowPromoBerita(true);
                  }}
                >
                  <img className="w-20 h-20 object-cover rounded-xl" src={promo.image} alt={promo.title} />
                  <div>
                    <span className={`text-xs ${promo.badgeColor} font-semibold px-2 py-1 rounded-full`}>
                      {promo.badge}
                    </span>
                    <h4 className="font-bold text-gray-800 mt-2">{promo.title}</h4>
                    <p className="text-sm text-gray-500">{promo.description}</p>
                  </div>
                </div>
                ))
              )}
            </div>
          </section>
          
          {/* Extra spacing for bottom navigation */}
          <div className="h-8"></div>
        </main>

        {/* Bottom Navigation */}
        <footer className="fixed bottom-0 left-0 right-0 max-w-lg mx-auto bg-white pt-2">
          <div className="bg-primary mx-4 mb-4 rounded-2xl shadow-lg">
            <div className="flex justify-around items-center h-20 text-white">
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'home' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => setActiveBottomTab('home')}
              >
                <i className="fa-solid fa-house text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Beranda</span>
              </span>
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'savings' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => {
                  setActiveBottomTab('savings');
                  setShowTabunganSummary(true);
                }}
              >
                <i className="fa-solid fa-piggy-bank text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Tabungan</span>
              </span>
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'investment' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => {
                  setActiveBottomTab('investment');
                  setShowInvestmentDetail(true);
                }}
              >
                <i className="fa-solid fa-arrow-trend-up text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Investasi</span>
              </span>
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'products' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => {
                  setActiveBottomTab('products');
                  window.open('/katalog', '_blank');
                }}
              >
                <i className="fa-solid fa-box-archive text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Katalog</span>
              </span>
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'account' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => {
                  setActiveBottomTab('account');
                  setShowAccountPage(true);
                }}
              >
                <i className="fa-solid fa-user-circle text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Akun</span>
              </span>
            </div>
          </div>
        </footer>
      </div>
    </section>
  );
};

export default MemberArea;

